// utils/styles/CommonStyle.js
import colors from "../../theme/colors";
import { Theme } from "../../theme/Theme"; // keep if you use it elsewhere

export const commonStyles = {
  /* =========================
     Motion tokens (additive)
  ========================== */
  motionFast: "400ms",
  motionEase: "cubic-bezier(0.25, 1, 0.5, 1)",

  /* =========================
     Layout / Shell  (kept)
  ========================== */
  dashboardRoot: { minHeight: "100vh" },
  dashboardSidebar: { minWidth: 230, minHeight: "100vh" },
  dashboardMain: { minHeight: "100vh" },

  /* =========================
     NEW: Viewport-locked layout + scroll areas
     - Use on DashBoard root row: layoutRoot
     - Wrap Sidebar with desktopSidebarShell (non-scrolling)
     - Make main column the scroller via mainScroll
  ========================== */
  layoutRoot: {
    height: "100vh",
    overflow: "hidden", // prevent body/page from scrolling
  },


  // utils/styles/CommonStyle.js (add these keys)
  headerBar: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
    position: "sticky",
    top: 0,
    borderBottom: "1px solid var(--border-header)",
    background: "var(--bg-main)",
    color: "var(--text-dark)",
    boxShadow: "var(--shadow-surface)",
    padding: "12px 16px",
    zIndex: 5,
  },


  headerChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 14px",
    border: "1px solid var(--border-light)",
    borderRadius: 12,
    background: "var(--bg-surface)",
    ...Theme.font14SemiBold,
    color: "var(--text-dark)",
    cursor: "pointer",
    boxShadow: "var(--shadow-card)",
  },

  headerChipIcon: {
    color: "var(--brand-violet)",
    display: "flex",
    alignItems: "center",
  },

  headerChipArrow: {
    marginLeft: "auto",
    color: "var(--text-subtle)",
    display: "flex",
    alignItems: "center",
  },

  desktopSidebarShell: {
    position: "sticky",
    top: 0,
    height: "100vh",
    overflow: "hidden", // sidebar itself won't scroll
    background: "var(--bg-sidebar)",
    borderRight: "1px solid var(--border-subtle)",
    color: "var(--text-dark)",
    display: "flex",
    flexDirection: "column",
  },
  // If you ever want the sidebar to scroll internally, switch overflow to 'auto'
  // desktopSidebarShell: { ... , overflowY: 'auto' }

  mainScroll: {
    height: "100vh",
    overflowY: "auto",
    overflowX: "hidden",

    WebkitOverflowScrolling: "touch",
  },

  /* ====== Sidebar container (right divider line) ====== */



  root: {
    background: "var(--bg-sidebar)",
    height: "100dvh",
    borderRight: "1px solid var(--border-subtle)",
    color: "var(--text-dark)",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    padding: "16px 16px clamp(16px, 3vh, 32px)",
    boxSizing: "border-box",
    overflowY: "auto",
  },



  stepRoot: {
    background: "var(--bg-sidebar)",
    height: "100dvh",
    borderRight: "1px solid var(--border-subtle)",
    color: "var(--text-dark)",
    display: "flex",
    flexDirection: "column",
    padding: "16px 16px clamp(16px, 3vh, 32px)",
    boxSizing: "border-box",
    overflowY: "auto",
  },


  sidebarTab: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    gap: 10,
    width: "100%",
    padding: "12px 16px",
    background: "transparent",
    border: "none",
    outline: "none",
    borderRadius: 14,
    borderRight: "3px solid transparent", // keeps space but invisible when inactive
    color: "var(--text-dark)",
    ...Theme.font14SemiBold,
    fontWeight: 600,
    textAlign: "left",
    cursor: "pointer",
  },

  sidebarTabActive: {
    color: colors.themeColor,
    background: colors.lightThemeColor,
    borderRight: `3px solid ${colors.themeColor}`, // visible only when active
    fontWeight: 600,
  },
  //  userBlock: {
  //   marginTop: "auto",          // pushes it to bottom naturally
  //   paddingBottom: "clamp(16px, 4vh, 40px)", // adjusts spacing based on screen height
  //   display: "flex",
  //   alignItems: "center",
  //   gap: 8,
  //   ...Theme.font14SemiBold,
  // },
  userBlock: {
    marginTop: "auto",
    display: "flex",
    alignItems: "center",
    gap: 8,
    ...Theme.font14SemiBold,
    paddingTop: "12px",
    borderTop: "1px solid var(--border-subtle)", // optional nice divider
  },


  logoutButton: {
    border: "none",
    background: "transparent",
    color: "var(--text-subtle)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 6,
    borderRadius: 8,
    cursor: "pointer",
    transition: "background 0.2s ease, color 0.2s ease",
  },

  textPurple: { color: "var(--brand-primary)" },

  /* =========================
     Page column (kept)
  ========================== */
  feedWrapper: { padding: "4px 0px" },
  feedCenter: { maxWidth: 720, width: "100%", margin: "0 auto", textAlign: "left" },
  feedCenter2: { maxWidth: 1400, width: "100%", margin: "0 auto", textAlign: "left" },

  /* =========================
     Chips (kept)
  ========================== */
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 14px",
    border: "1px solid var(--border-subtle)",
    borderRadius: 999,
    background: "var(--bg-surface)",
    color: "var(--text-subtle)",
    fontSize: 14,
    lineHeight: 1,
  },

  /* =========================
     Sticky Tab Bar
     Usage: style={{ ...commonStyles.stickyTabBar, top: navbarHeight }}
     navbarHeight comes from useNavbarHeight()
  ========================== */
  stickyTabBar: {
    position: "sticky",
    zIndex: 4,
    background: "var(--bg-main)",
    // backdropFilter: "blur(10px)",
    // WebkitBackdropFilter: "blur(10px)",
  },



  /* =========================
     Tabs (Desktop)  (kept + additive)
  ========================== */
  tabBar: {
    position: "relative",
    display: "flex",
    gap: 28,
    alignItems: "center",
    justifyContent: "space-around",
    paddingBottom: 0,
    marginBottom: 8,
    background: "var(--bg-main)",
  },
  tabTrack: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    height: 2,
    background: "var(--border-brand-subtle)",
    borderRadius: 2,
  },
 tabLink: {
  position: "relative",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  background: "transparent",
  border: 0,
  padding: "12px 20px ",
  flex: 1,
  minHeight: "48px",
  cursor: "pointer",
  transition: "color .25s ease",
  ...Theme.font14Regular,
},
  tabLinkActive: { ...Theme.font14SemiBold, color: colors.purple, },

  // kept from your original (even if unused now)
  tabUnderline: {
    position: "absolute",
    left: "50%",
    transform: "translateX(-50%)",
    bottom: 0,
    width: "100%", // set 60% if you want shorter underline
    height: 3,
    borderRadius: 3,
    background: colors.purple,
  },

  // single sliding underline indicator (additive, non-breaking)
  tabIndicator: {
    position: "absolute",
    bottom: 0,
    left: 0,
    width: "50%", // two tabs => half width
    height: 3,
    borderRadius: 3,
    background: colors.purple,
    transform: "translateX(0%) scaleX(0.98)",
    opacity: 1,
    transition:
      "transform 400ms cubic-bezier(0.25,1,0.5,1), opacity 250ms ease-out",
    willChange: "transform, opacity",
  },

  /* =========================
     Mobile segmented toggle (kept + additive)
  ========================== */
  segToggle: {
    position: "relative",
    background: "var(--bg-surface-2)",
    border: "1px solid var(--border-subtle)",
    borderRadius: 12,

    padding: 4,
    gap: 4,
    marginBottom: 12,
    display: "flex",
    overflow: "hidden",
  },
  segBtn: {
    ...Theme.font14SemiBold,
    flex: 1,
    border: 0,
    background: "transparent",
    borderRadius: 10,
    padding: "8px 10px",
    fontWeight: 600,
    fontSize: 14,
    color: "var(--text-primary)",
    cursor: "pointer",
    transition: "all .25s ease",
    position: "relative",
    zIndex: 2,
  },
  segBtnActive: {
    color: "var(--text-on-brand)",
  },
  bookingTabsWrapper: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    marginBottom: 20,

    // padding: 6,
    // borderRadius: 12,
    // border: "1px solid #E2E8F0",
  },

  bookingTabBtn: {
    padding: "8px 12px",
    cursor: "pointer",
    background: "transparent",
    ...Theme.font14SemiBold,
    color: "var(--text-primary)",
    transition: "all 0.25s ease",
    borderRadius: 12,
    border: "1px solid var(--border-subtle)",
  },


  bookingTabBtnActive: {
    background: "var(--bg-surface-2)",
    border: "1px solid " + colors.borderPurple,
    color: colors.themeColor,
  },

  // animated background pill (additive)
  segIndicator: {
    position: "absolute",
    top: 4,
    bottom: 4,
    left: 4,
    width: "calc(50% - 4px)", // two tabs
    borderRadius: 10,
    background: colors.themeColor,
    boxShadow: "var(--shadow-brand)",
    transform: "translateX(0%) scale(0.98)",
    transition:
      "transform 400ms cubic-bezier(0.25,1,0.5,1), opacity 250ms ease-out",
    willChange: "transform, opacity",
  },

  /* =========================
     Controls (kept)
  ========================== */
  controlRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    margin: "8px 0 2px",
    flexWrap: "nowrap",
  },
  searchChip: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: 6,
    height: 40,
    minWidth: 0,
    border: "1px solid var(--border-light)",
    borderRadius: 12,
    background: "var(--bg-surface)",
    flexGrow: 1,
  },
  searchInput: {
    flex: 1,
    minWidth: 0,
    border: 0,
    outline: "none",
    background: "transparent",
    fontSize: 14,
    color: "var(--text-search)",
  },
  searchGo: {
    width: 34,
    height: 34,
    flexShrink: 0,
    border: 0,
    borderRadius: 999,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "var(--brand-violet)",
    color: "var(--text-on-brand)",
    cursor: "pointer",
    transition: "transform .15s ease",
  },
  pillBtnSm: {
    height: 40,
    padding: "0 12px",
    border: "1px solid var(--border-light)",
    borderRadius: 12,
    background: "var(--bg-surface)",
    color: "var(--text-subtle)",
    fontSize: 14,
    fontWeight: 500,
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    cursor: "pointer",
    flexShrink: 0,
  },

  /* =========================
     Cards (kept)
  ========================== */
  feedCard: { maxWidth: 430, border: "1.5px solid var(--border-subtle)", borderRadius: 16 },

  /* =========================
     Meta (kept)
  ========================== */
  resultCount: { color: "var(--text-muted)", fontSize: 13 },

  /* =========================
     Mobile Topbar & Drawer (kept)
  ========================== */
  mobileTopbar: {
    height: 56,
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "0 12px",
    borderBottom: "1px solid var(--border-subtle)",
    background: "var(--bg-surface)",
    color: "var(--text-dark)",
    position: "sticky",
    top: 0,
    zIndex: 10,
  },
  hamburger: {
    width: 38,
    height: 38,
    border: "1px solid var(--border-subtle)",
    borderRadius: 10,
    background: "var(--bg-surface)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 22,
    color: "var(--text-primary)",
  },

  drawer: {
    position: "fixed",
    top: 0,
    left: 0,
    height: "100dvh",
    width: 290,
    background: "var(--bg-drawer)",
    color: "var(--text-dark)",
    boxShadow: "var(--shadow-overlay)",
    transform: "translateX(-100%)",
    transition: "transform .25s ease",
    zIndex: 1040,

    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",

    overflowY: "auto",
    WebkitOverflowScrolling: "touch",
    paddingBottom: "env(safe-area-inset-bottom)",
  },



  drawerOpen: { transform: "translateX(0)" },
  drawerHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 12px",
    borderBottom: "1px solid var(--border-subtle)",
  },
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "var(--overlay-backdrop-light)",
    border: 0,
    zIndex: 1030,
  },

  /* =========================
     Shared inputs / borders (kept)
  ========================== */
  input: {
    width: "100%",
    height: 44,
    borderRadius: 12,
    border: `1px solid ${colors.inputBorderColor}`,
    fontSize: 16,
    padding: "0 14px",
    outline: "none",
    fontFamily: "inherit",
    transition: "border 0.2s",
    boxSizing: "border-box",
    background: "var(--bg-surface)",
    color: "var(--text-primary)",
  },
  commonBorder: { border: `1.5px solid ${colors.inputBorderColor}` },
  errorBorder: { border: `1.5px solid ${colors.danger}` },

  /* =========================
     Containers (kept)
  ========================== */
  container: {
    minHeight: "100vh",
    width: "100vw",
    padding: 20,
    boxSizing: "border-box",
    background: "var(--bg-auth-container)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  boxContainer: {
    width: "100%",
    maxWidth: 400,
    background: "var(--bg-surface)",
    borderRadius: 20,
    padding: "30px 32px 32px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    boxSizing: "border-box",
  },
  headerContainer: {
    width: "100%",
    padding: 16,
    backgroundColor: "var(--bg-surface-2)",
    borderBottom: "1px solid var(--border-light)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 20,
  },


  // -- profile section --//


  PfeedCenter: { maxWidth: 400, width: "100%", margin: "0 auto" },

  surfaceCard: {
    background: "var(--bg-surface)",
    color: "var(--text-primary)",
    borderRadius: 14,
    border: "1px solid var(--border-card)",
    padding: 12,
    boxShadow: "var(--shadow-card)",
  },

  clickRow: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    gap: 4,
    padding: "4px 14px",
    borderRadius: 12,
    background: "var(--bg-surface)",
    border: "1px solid var(--border-card)",
    color: "var(--text-primary)",
    textAlign: "left",
    boxShadow: "var(--shadow-card)",
    cursor: "pointer",
    height: 46
  },


  iconSquareSm: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 36,
    height: 36,
    borderRadius: 8,
    flex: "0 0 auto",
  },

  hrDashed: {
    border: 0,
    borderTop: "1px dashed var(--border-subtle)",
    margin: "14px 0",
  },

};
